import React, {useEffect, useState} from 'react';
import {
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {connect} from 'react-redux';
import {IRootState} from 'app/shared/reducers';
import {
  getHealthcareFacilitiesCompact,
  getStartCouncilSuggestions,
  getStartFacilitySuggestions,
  getStartVillageSuggestions,
  reset as resetHealthcareFacility
} from 'app/entities/healthcare-facility/healthcare-facility.reducer';
import {getVillagesCompact, reset as resetVillage} from 'app/entities/village/village.reducer';
import {getAllByType as getCountries} from 'app/entities/region/region.reducer';
import {IRegion} from 'app/shared/model/region.model';
import {hasAnyPermission} from 'app/shared/auth/permissions';
import VillageAvTypeahead from 'app/shared/layout/menus/village-av-typeahead';
import {AvField, AvForm, AvGroup} from 'availity-reactstrap-validation';
import {LocationType, PopupType} from './journey-info';
import {IJourney} from "app/shared/model/journey.model";
import RRHFModal from './rr-hf-modal';
import {convertSecondsToMinutes} from "app/shared/util/date-utils";
import {getMMamaSettings} from "app/modules/administration/mmama-settings/mmama-settings.reducer";
import {IMMamaSettings} from 'app/shared/model/mmama-settings-model';
import {IHealthcareFacility} from 'app/shared/model/healthcare-facility.model';
import routeService from "app/entities/route/route.service";
import {IRoute} from "app/shared/model/route.model";
import geofenceRouteService from "app/entities/geofence-route/geofence-route.service";
import {RouteStartType} from "app/shared/model/enumerations/route-start-type.model";
import {RouteEndType} from "app/shared/model/enumerations/route-end-type.model";
import {
  getEntitiesByPatientCase as getRejectedFacilitiesByPatientCase
} from 'app/entities/rejection-reason-hf/patient-case-rr-hf.reducer';
import {getEntities as getRejectionReasons} from "app/entities/rejection-reason-hf/rejection-reason.reducer";
import {useRegionLabels} from 'app/shared/hooks/useRegionLabels';
import {RegionDescriptionType} from 'app/shared/model/enumerations/region-description-type.model';
import {fromCents, toCents} from "app/shared/util/money-utils";
import {round} from "lodash";

interface LocationSelectionModalProps extends StateProps, DispatchProps {
  isOpen: boolean;
  toggle: () => void;
  onSave: (locationType: string, selectedLocation: any, totalCost?: number, totalDistance?: number, totalDurationSeconds?: number, routeId?: string, geofenceRoute?: boolean) => void;
  journey: IJourney;
  popupType: PopupType;
}

interface FacilitySuggestion {
  facility: IHealthcareFacility;
  distance: number;
  durationSeconds: number;
  routeCouncil?: IRoute;
  route?: IRoute;
}

const LocationSelectionModalComponent = (props: LocationSelectionModalProps) => {
  const {isOpen, toggle, onSave, journey, popupType} = props;
  const {getLabel} = useRegionLabels();
  const {
    compactHealthcareFacilities,
    compactVillages,
    countries,
    facilitiesLoading,
    villagesLoading,
    mmamaSettings,
    startCouncilSuggestions,
    startFacilitySuggestions,
    startVillageSuggestions,
    hfSuggestionsLoading,
    rejectedHealthcareFacilities,
    rejectionReasonsHF
  } = props;

  const [locationType, setLocationType] = useState<LocationType>(journey.startVillage && popupType === PopupType.START ? LocationType.VILLAGE : LocationType.HEALTHCARE_FACILITY);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeCountry, setActiveCountry] = useState<IRegion>({});
  const [clearValue, setClearValue] = useState(false);
  const [hfNames, setHfNames] = useState([]);
  const [villageNames, setVillageNames] = useState([]);
  const [durationMinutes, setDurationMinutes] = useState(0);
  const [existingRoute, setExistingRoute] = useState<IRoute>(null);
  const [routeDistance, setRouteDistance] = useState<number>(0.0);
  const [overridePrice, setOverridePrice] = useState<number>(0);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [showRouteDetails, setShowRouteDetails] = useState(false);
  const [countrySettings, setCountrySettings] = useState<IMMamaSettings | null>(null);
  const [facilitySuggestions, setFacilitySuggestions] = useState<FacilitySuggestion[]>([]);
  const [showRRHFModal, setShowRRHFModal] = useState(false);
  const [rejectionHFId, setRejectionHFId] = useState<string>('');
  const [loadingData, setLoadingData] = useState(false);
  const [collapsedLevels, setCollapsedLevels] = useState<{ [key: number]: boolean }>({});
  const [isUsingGeofenceRoute, setIsUsingGeofenceRoute] = useState<boolean>(false);
  const [loadingFacilityId, setLoadingFacilityId] = useState<string | null>(null);

  const patientCountryId = journey?.patientCase?.patient?.countryId;

  const getContactNumbers = (locationEntity?: IHealthcareFacility): string[] => {
    if (!locationEntity) return [];
    return [
      locationEntity.phoneNumber,
      locationEntity.phone_number_2 || (locationEntity as any).phoneNumber2,
      locationEntity.phone_number_3 || (locationEntity as any).phoneNumber3
    ].filter(Boolean) as string[];
  };

  // Helper function to check if a healthcare facility is rejected
  const isHealthcareFacilityRejected = (facilityId: string): boolean => {
    if (!rejectedHealthcareFacilities || !facilityId) {
      return false;
    }
    return rejectedHealthcareFacilities.some(rejection =>
      rejection.hfId === facilityId || rejection.healthcareFacility?.id === facilityId
    );
  };

  // Helper function to get rejection reason for a healthcare facility
  const getRejectionReason = (facilityId: string): string | null => {
    if (!rejectedHealthcareFacilities || !facilityId) {
      return null;
    }
    const rejection = rejectedHealthcareFacilities.find(r =>
      r.hfId === facilityId || r.healthcareFacility?.id === facilityId
    );

    const rrHF = rejectionReasonsHF?.find(rr => rr.id === rejection?.rrHfId);
    const reasonText = `${rrHF ? rrHF.enRejectionReason : ''}${rejection?.description ? ` - ${rejection.description}` : ''}`;
    return reasonText !== '' ? reasonText : 'No reason provided';
  };

  const getRequiredFacilityLevel = (motherSymptoms, babySymptoms, startFacility?, enableSameFacilityLevel = countrySettings?.enableSameFacilityLevel) => {
    // If the case is from a facility, use the first required level; otherwise, use the second
    const isFromFacility = !!startFacility;

    const requiredLevels = [
      ...motherSymptoms.map(symptom =>
        isFromFacility ? symptom.requiredFacilityLevel : symptom.requiredFacilityLevel2
      ),
      ...babySymptoms.map(symptom =>
        isFromFacility ? symptom.requiredFacilityLevel : symptom.requiredFacilityLevel2
      )
    ];
    if (startFacility && typeof startFacility.level === 'number') {
      requiredLevels.push(enableSameFacilityLevel ? startFacility.level : startFacility.level + 1);
    }
    const mostSevere = requiredLevels.length > 0 ? Math.max(...requiredLevels) : 1;
    return mostSevere;
  };

  // When showRRHFModal closes fetch the rejected healthcare facilities again
  useEffect(() => {
    if (!showRRHFModal) {
      if (journey?.patientCase?.id) {
        props.getRejectedFacilitiesByPatientCase(journey.patientCase.id);
      }
    }
  }, [showRRHFModal]);

  useEffect(() => {
    // Only initialize when modal is actually open
    if (isOpen) {
      props.getCountries();
      props.getMMamaSettings();
      // Fetch rejected healthcare facilities for this patient case
      if (journey?.patientCase?.id && popupType === PopupType.END) {
        props.getRejectedFacilitiesByPatientCase(journey.patientCase.id);
        if (!rejectionReasonsHF || rejectionReasonsHF.length === 0) {
          props.getRejectionReasons();
        }
      }
    }
  }, [isOpen]);

  // Reset all state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all local state
      setLocationType(journey.startVillage && popupType === PopupType.START ? LocationType.VILLAGE : LocationType.HEALTHCARE_FACILITY);
      setSelectedLocation(null);
      setActiveCountry({});
      setClearValue(prev => !prev);
      setHfNames([]);
      setVillageNames([]);
      setDurationMinutes(0);
      setExistingRoute(null);
      setRouteDistance(0.0);
      setOverridePrice(0);
      setCurrentPrice(0);
      setShowRouteDetails(false);
      setCountrySettings(null);
      setFacilitySuggestions([]);
      setShowRRHFModal(false);
      setRejectionHFId('');
      setLoadingData(false);
      setCollapsedLevels({});
      setIsUsingGeofenceRoute(false);
      setLoadingFacilityId(null);

      // Reset Redux state
      props.resetHealthcareFacility();
      props.resetVillage();
    }
  }, [isOpen, journey.startVillage]);

  // Reset on unmount
  useEffect(() => {
    return () => {
      props.resetHealthcareFacility();
      props.resetVillage();
    };
  }, []);

  useEffect(() => {
    if (mmamaSettings && mmamaSettings.length > 0 && activeCountry.id) {
      const settings = mmamaSettings.find(setting => setting.country.id === activeCountry.id);
      if (settings) {
        setCountrySettings(settings);
      }
    }
  }, [mmamaSettings, activeCountry]);

  useEffect(() => {
    if (isOpen && countrySettings?.id && popupType === PopupType.END && locationType === LocationType.HEALTHCARE_FACILITY) {
      const requiredLevel = getRequiredFacilityLevel(
        journey.patientCase?.motherSymptoms || [],
        journey.patientCase?.babySymptoms || [],
        journey.startHealthcareFacility || null,
        countrySettings.enableSameFacilityLevel
      );
      if (countrySettings.useGeofence) {
        if (journey.startVillage) {
          props.getStartCouncilSuggestions(journey.startVillage.council.id, requiredLevel);
        } else if (journey.startHealthcareFacility) {
          props.getStartCouncilSuggestions(journey.startHealthcareFacility.council.id, requiredLevel);
        }
      } else {
        if (journey.startVillage) {
          props.getStartVillageSuggestions(journey.startVillage.id, requiredLevel);
        } else if (journey.startHealthcareFacility) {
          props.getStartFacilitySuggestions(journey.startHealthcareFacility.id, requiredLevel);
        }
      }
    }
  }, [isOpen, locationType, countrySettings]);

  useEffect(() => {
    // format suggestions FacilitySuggestion
    if (startFacilitySuggestions && startFacilitySuggestions.length > 0) {
      const formattedSuggestions = startFacilitySuggestions
        .filter(suggestion => !journey.startHealthcareFacility?.id || suggestion.healthcareFacility?.id !== journey.startHealthcareFacility.id)
        .map(suggestion => ({
          facility: suggestion.healthcareFacility,
          distance: suggestion.route?.distance,
          durationSeconds: suggestion.route?.durationSeconds,
          routeCouncil: null,
          route: suggestion.route
        }));
      setFacilitySuggestions(formattedSuggestions);
    } else if (startVillageSuggestions && startVillageSuggestions.length > 0) {
      const formattedSuggestions = startVillageSuggestions.map(suggestion => ({
        facility: suggestion.healthcareFacility,
        distance: suggestion.route?.distance,
        durationSeconds: suggestion.route?.durationSeconds,
        routeCouncil: null,
        route: suggestion.route
      }));
      setFacilitySuggestions(formattedSuggestions);
    } else if (startCouncilSuggestions && startCouncilSuggestions.length > 0) {
      const formattedSuggestions = startCouncilSuggestions
        .filter(suggestion => !journey.startHealthcareFacility?.id || suggestion.healthcareFacility?.id !== journey.startHealthcareFacility.id)
        .map(suggestion => ({
          facility: suggestion.healthcareFacility,
          distance: suggestion.routeCouncil?.distance,
          durationSeconds: suggestion.routeCouncil?.durationSeconds,
          routeCouncil: suggestion.routeCouncil,
          route: suggestion.route
        }));
      setFacilitySuggestions(formattedSuggestions);
    }
  }, [startCouncilSuggestions, startFacilitySuggestions, startVillageSuggestions, journey.startHealthcareFacility]);

  useEffect(() => {
    // Only process when modal is open and we have countries data
    if (isOpen && countries && countries.length > 0 && patientCountryId) {
      const ac = countries.find(country => country.id === patientCountryId) || countries[0];

      // Only update if the country has changed
      if (activeCountry?.id !== ac?.id) {
        setActiveCountry(ac);

        // Load facilities and villages based on selected country
        if (ac?.id) {
          props.getHealthcareFacilitiesCompact(ac.id);
          props.getVillagesCompact(ac.id);
        }
      }
    }
  }, [isOpen, countries, patientCountryId, activeCountry]);

  // Format healthcare facilities for typeahead
  useEffect(() => {
    if (compactHealthcareFacilities.length > 0) {
      const healthcareFacilityNames = compactHealthcareFacilities
        .filter(hf => hf?.countryId === activeCountry?.id)
        .map(item => ({
          short: item.name,
          council: item?.councilName,
          latitude: item?.locationLat,
          longitude: item?.locationLon,
          district: item?.districtName,
          full: item.name + ' / ' + item?.councilName + ' / ' + item?.districtName,
          id: item.id,
          fullObject: item
        }));
      setHfNames(healthcareFacilityNames);
    }
  }, [compactHealthcareFacilities, activeCountry]);

  // Format villages for typeahead
  useEffect(() => {
    if (compactVillages.length > 0) {
      const villageOptions = compactVillages
        .filter(village => village?.countryId === activeCountry?.id)
        .map(item => ({
          short: item.name,
          council: item?.councilName,
          district: item?.districtName,
          full: item.name + ' / ' + item?.councilName + ' / ' + item?.districtName,
          id: item.id,
          fullObject: item
        }));
      setVillageNames(villageOptions);
    }
  }, [compactVillages, activeCountry]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setClearValue(prev => !prev); // Reset the typeahead
    }
  }, [isOpen, journey]);

  // Helper function to create option lists for typeahead
  const optionList = (includeEmpty = false) => {
    let result = includeEmpty ? [{short: '', full: '', id: undefined}] : [];
    if (locationType === LocationType.HEALTHCARE_FACILITY) {
      result = result.concat(hfNames);
    } else {
      result = result.concat(villageNames);
    }
    return result;
  };

  const getRouteDistance = async (startLocation, endLocation) => {
    if (!startLocation?.id || !endLocation?.id) {
      alert('Start or end location is missing ID');
      setShowRouteDetails(true);
      return;
    }

    // Both locations are villages (this function is only called for village-to-village)
    let locationApiFailed = false;
    let locationData = null;
    try {
      locationData = await routeService.getLocationDirections(
        undefined,
        startLocation.id,
        undefined,
        endLocation.id
      );
    } catch (locationApiError) {
      console.error('Backend directions API error:', locationApiError);
      locationApiFailed = true;
    }

    // Check if location api failed or returned no routes
    if (locationApiFailed || !locationData) {
      if (!countrySettings.useGeofence) {
        setLoadingData(false);
        alert('No routes found for the given locations');
        return;
      }

      console.log('Location API failed or no distance found, trying geofence route fallback');

      // Try to get council IDs from start and end locations
      const startCouncilId = startLocation.council?.id || startLocation.councilId;
      const endCouncilId = endLocation.council?.id || endLocation.councilId;

      if (startCouncilId && endCouncilId) {
        try {
          let geofenceRoutes = await geofenceRouteService.searchEntities(startCouncilId, endCouncilId, 0, 1);

          if ((!geofenceRoutes || geofenceRoutes.length === 0) && startCouncilId !== endCouncilId) {
            geofenceRoutes = await geofenceRouteService.searchEntities(endCouncilId, startCouncilId, 0, 1);
          }

          if (geofenceRoutes && geofenceRoutes.length > 0) {
            // Get the first active route or just the first route
            const geofenceRoute = geofenceRoutes.find(route => route.isActive) || geofenceRoutes[0];

            // Use geofence route data
            setRouteDistance(geofenceRoute.distance || 0);
            setDurationMinutes(geofenceRoute.durationSeconds ? Math.floor(geofenceRoute.durationSeconds / 60) : 0);

            // Calculate price: use override price if available, otherwise calculate from distance
            const price = geofenceRoute.overridePrice
              ? geofenceRoute.overridePrice
              : round((geofenceRoute.distance || 0) * countrySettings.costKm, 0);
            setCurrentPrice(price);

            // Store geofence route to pass as routeCouncil
            setIsUsingGeofenceRoute(true);
            setShowRouteDetails(false);
            setLoadingData(false);
            return;
          }
        } catch (error) {
          console.error('Error fetching geofence route:', error);
        }
      }

      // If geofence route also failed
      setLoadingData(false);
      alert('No routes found for the given locations');
      return;
    }

    // Mapbox succeeded
    setIsUsingGeofenceRoute(false);
    setRouteDistance(round(locationData.distance, 2));
    setDurationMinutes(convertSecondsToMinutes(locationData.durationSeconds.toString()));

    // Dont need to calculate effective cost km since no driver is assigned yet
    const price = parseFloat((locationData.distance * countrySettings.costKm).toFixed(2));
    setCurrentPrice(round(price, 0));
    setShowRouteDetails(true);
    setLoadingData(false);
  };

  const handleLocationSelection = async (selectedId: string) => {
    if (!selectedId) {
      setSelectedLocation(null);
      return;
    }

    if (locationType === LocationType.HEALTHCARE_FACILITY) {
      const facility = hfNames.find(item => item.id === selectedId);
      if (facility) {
        setSelectedLocation(facility.id);
      } else {
        alert('Selected healthcare facility not found. Please try again.');
        return;
      }
    } else {
      const village = villageNames.find(item => item.id === selectedId);
      if (village) {
        setSelectedLocation(village.id);
        if (popupType === PopupType.END) {
          // Get start and end lat long and calculate route distance
          if (journey.startVillage) {
            try {
              setLoadingData(true);
              const villageToVillageRoutes = await routeService.getRoutesFromVillageToVillage(
                village.id,
                journey.startVillage.id
              );
              if (villageToVillageRoutes.length > 0) {
                setLoadingData(false);
                setExistingRoute(villageToVillageRoutes[0]);
              } else {
                await getRouteDistance(journey.startVillage, village.fullObject);
              }
            } catch (error) {
              setLoadingData(false);
              alert('An error occurred while fetching routes. Please try again.');
              console.error(error);
            }
          } else {
            // Can't have a facility to village journey
            alert('Oops! Something went wrong. Please try again.');
          }
        }
      } else {
        alert(`Selected ${getLabel(RegionDescriptionType.VILLAGE).toLowerCase()} not found. Please try again.`);
        return;
      }
    }
  };

  const handleSave = (locationId: string, route: IRoute, routeCouncil?: IRoute) => {
    if (locationId) {
      if (popupType === PopupType.START) {
        // Pickup Selection
        onSave(locationType, locationId);
        toggle();
      } else {
        // Destination Selection
        if (!route?.id && popupType === PopupType.END && journey.startVillage?.id && locationType === LocationType.VILLAGE
          && (!countrySettings.useGeofence || countrySettings.geofenceSaveRoutes) && !isUsingGeofenceRoute) {
          // If we don't have an existing route when the user selects a destination for village to village journey
          // and the user is not in geofence mode or user is in geofence mode and save routes is enabled.
          // Don't enter this section if mapbox api failed and we are using geofence route
          const newRoute: IRoute = {
            startType: RouteStartType.VILLAGE,
            endType: RouteEndType.VILLAGE,
            startVillageId: journey.startVillage.id,
            endVillageId: locationId,
            distance: routeDistance,
            durationSeconds: durationMinutes * 60,
            overridePrice: overridePrice || null,
            isActive: true,
          };

          routeService.createRoute(newRoute)
            .then(response => {
              if (response?.id) {
                onSave(
                  locationType,
                  locationId,
                  overridePrice || currentPrice || null,
                  routeDistance,
                  durationMinutes * 60,
                  response.id,
                  isUsingGeofenceRoute
                );
                toggle();
              } else {
                alert('Failed to create route. Please try again.');
              }
            })
            .catch(error => {
              setLoadingData(false);
              alert('An error occurred while creating the route. Please try again.');
            });
        } else if (route?.id) {
          // Existing route
          const routeCurrentPrice = route?.distance ? round(route.distance * countrySettings.costKm, 0) : null;
          onSave(
            locationType,
            locationId,
            route?.overridePrice || routeCurrentPrice || null,
            route?.distance || null,
            route?.durationSeconds || null,
            route?.id,
            isUsingGeofenceRoute
          );
          toggle();
        } else if (countrySettings.useGeofence) {
          // Geofence on
          if (popupType === PopupType.END && journey.startVillage?.id && locationType === LocationType.VILLAGE) {
            // Village to village without existing route
            onSave(
              locationType,
              locationId,
              overridePrice || currentPrice || null,
              routeDistance,
              durationMinutes * 60,
              null,
              isUsingGeofenceRoute
            );
          } else {
            // Facility destination (geofence on): routeCouncil is populated with Mapbox data
            const routeCouncilCurrentPrice = routeCouncil?.distance ? round(routeCouncil.distance * countrySettings.costKm, 0) : null;
            const facilityRouteCost = routeCouncil?.overridePrice || routeCouncilCurrentPrice || null;

            if (countrySettings.geofenceSaveRoutes && !isUsingGeofenceRoute) {
              // Create a route record for the facility destination
              const startType = journey.startHealthcareFacility?.id ? RouteStartType.HEALTHCARE_FACILITY : RouteStartType.VILLAGE;
              const newRoute: IRoute = {
                startType,
                endType: RouteEndType.HEALTHCARE_FACILITY,
                startFacilityId: journey.startHealthcareFacility?.id || null,
                startVillageId: journey.startVillage?.id || null,
                endFacilityId: locationId,
                distance: routeCouncil?.distance || null,
                durationSeconds: routeCouncil?.durationSeconds || null,
                overridePrice: facilityRouteCost,
                isActive: true,
              };

              routeService.createRoute(newRoute)
                .then(response => {
                  onSave(
                    locationType,
                    locationId,
                    facilityRouteCost,
                    routeCouncil?.distance || null,
                    routeCouncil?.durationSeconds || null,
                    response?.id || null,
                    isUsingGeofenceRoute
                  );
                  toggle();
                })
                .catch(error => {
                  console.error('Failed to create facility route:', error);
                  // Still proceed even if route creation fails
                  onSave(
                    locationType,
                    locationId,
                    facilityRouteCost,
                    routeCouncil?.distance || null,
                    routeCouncil?.durationSeconds || null,
                    null,
                    isUsingGeofenceRoute
                  );
                  toggle();
                });
              return; // toggle() called inside promise
            } else {
              onSave(
                locationType,
                locationId,
                facilityRouteCost,
                routeCouncil?.distance || null,
                routeCouncil?.durationSeconds || null,
                null,
                isUsingGeofenceRoute
              );
            }
          }
          toggle();
        } else {
          alert('Oops! Something went wrong.');
        }
      }
    }
  };

  // For geofence=true, facility destination: fetch Mapbox distance/duration then save
  const handleGeofenceFacilitySelect = async (facilitySuggestion: FacilitySuggestion) => {
    if (!countrySettings?.useGeofence) {
      // Should not reach here without geofence, but guard anyway
      setSelectedLocation(facilitySuggestion.facility.id);
      handleSave(facilitySuggestion.facility.id, facilitySuggestion.route, facilitySuggestion.routeCouncil);
      return;
    }

    const facilityId = facilitySuggestion.facility.id;
    setLoadingFacilityId(facilityId);

    try {
      const locationData = await routeService.getLocationDirections(
        journey.startHealthcareFacility?.id || undefined,
        journey.startVillage?.id || undefined,
        facilitySuggestion.facility.id,
        undefined
      );

      if (!locationData) {
        setSelectedLocation(facilitySuggestion.facility.id);
        setLoadingFacilityId(null);
        handleSave(facilitySuggestion.facility.id, facilitySuggestion.route, facilitySuggestion.routeCouncil);
        return;
      }

      // Build a route council object from Mapbox data so handleSave's existing
      // geofence branch (which reads routeCouncil.distance / durationSeconds / overridePrice) works as-is
      const mapboxDistance = round(locationData.distance, 2);
      const mapboxRouteCouncil: IRoute = {
        distance: mapboxDistance,
        durationSeconds: locationData.durationSeconds,
        overridePrice: round(mapboxDistance * (countrySettings?.costKm || 0), 0),
      };

      setSelectedLocation(facilityId);
      setLoadingFacilityId(null);
      setIsUsingGeofenceRoute(false);
      handleSave(facilityId, null, mapboxRouteCouncil);
    } catch (error) {
      console.error('Error fetching Mapbox directions for facility:', error);
      setLoadingFacilityId(null);
      alert('Failed to fetch route for the selected facility. Please try again.');
    }
  };

  // Handle healthcare facility rejection
  const handleHFRejection = (hfId: string) => {
    setRejectionHFId(hfId);
    setShowRRHFModal(true);
  };

  // Handle closing rejection modal
  const toggleRRHFModal = () => {
    setShowRRHFModal(false);
    setRejectionHFId('');
  };

  const onLocationTypeChange = (type: LocationType) => {
    setLocationType(type);
    setSelectedLocation(null);
    setClearValue(prev => !prev); // Reset the typeahead
  };

  const toggleCollapseLevel = (level: number) => {
    setCollapsedLevels(prev => ({
      ...prev,
      [level]: !prev[level]
    }));
  };

  // Group facilities by level
  const groupFacilitiesByLevel = (suggestions: FacilitySuggestion[]) => {
    const maxPerLevel = countrySettings?.geofenceHfLimit || suggestions.length;

    const grouped = suggestions.reduce((acc, suggestion) => {
      const level = suggestion.facility.level || 1;
      if (!acc[level]) {
        acc[level] = [];
      }
      // Only add if we haven't reached the limit for this level
      if (acc[level].length < maxPerLevel) {
        acc[level].push(suggestion);
      }
      return acc;
    }, {} as { [key: number]: FacilitySuggestion[] });

    return grouped;
  };

  const isLoading = (locationType === LocationType.HEALTHCARE_FACILITY && facilitiesLoading) || (locationType === LocationType.VILLAGE && villagesLoading);

  // Health Facility Suggestion component
  const HealthFacilitySuggestion = ({facility, distance, durationSeconds, onSelect, onReject, isSelectLoading = false}) => {
    const isRejected = isHealthcareFacilityRejected(facility.id);
    const rejectionReason = getRejectionReason(facility.id);
    const contactNumbers = getContactNumbers(facility);

    return (
      <Card className={`mb-3 shadow-sm ${isRejected ? 'border-danger' : ''}`}>
        <CardBody>
          <Row>
            <Col xs="12">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-1">{facility.name}</h5>
                {isRejected && (
                  <span className="badge badge-danger dispatcher-badge">REJECTED</span>
                )}
              </div>
              <p className="text-muted mb-2">{facility.council?.name}, {facility.council?.parent?.name}</p>
            </Col>

            {contactNumbers.length > 0 && (
              <Col xs="12" className="mb-2 text-muted" style={{ fontSize: '14px' }}>
                {contactNumbers.length > 0 && (
                  <div>
                    <FontAwesomeIcon icon="phone-alt" className="mr-2"/>
                    <span>{contactNumbers.join(', ')}</span>
                  </div>
                )}
              </Col>
            )}

            {facility.description && (
              <Col xs="12" className="mb-2">
                <p className="mb-0 text-muted">{facility.description}</p>
              </Col>
            )}

            {isRejected && (
              <Col xs="12" className="mb-2">
                <div className="alert alert-warning mb-0">
                  <FontAwesomeIcon icon="exclamation-triangle" className="mr-2"/>
                  <strong>Rejection Reason:</strong> {rejectionReason}
                </div>
              </Col>
            )}

            <Col xs="12" className="mb-3">
              <Row>
                <Col xs="6" md="4">
                  <FontAwesomeIcon icon="map-marker-alt" className="text-primary mr-1"/>
                  <span className="text-muted">Route</span>
                  <div className="font-weight-bold">~{distance?.toFixed(2)}km</div>
                </Col>
                <Col xs="6" md="4">
                  <FontAwesomeIcon icon="clock" className="text-primary mr-1"/>
                  <span className="text-muted">Est. Time</span>
                  <div
                    className="font-weight-bold">{durationSeconds ? `~${convertSecondsToMinutes(durationSeconds).toFixed(1)} min` : 'N/A'}</div>
                </Col>
              </Row>
            </Col>

            <Col xs="12">
              <Row>
                <Col xs="12" md="6" className="mb-2 mb-md-0">
                  <Button
                    color="danger"
                    outline
                    onClick={onReject}
                    className="px-4 w-100"
                    disabled={isRejected || isSelectLoading}
                  >
                    UNABLE TO ACCEPT PATIENT
                  </Button>
                </Col>
                <Col xs="12" md="6">
                  <Button
                    color="primary"
                    outline
                    onClick={onSelect}
                    className="px-4 w-100"
                    disabled={isRejected || isSelectLoading}
                  >
                    {isSelectLoading ? (
                      <><FontAwesomeIcon icon="spinner" spin className="mr-1"/> Loading...</>
                    ) : 'SELECT'}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  };

  // Patient Information Component
  const PatientInformation = ({patientCase}) => {
    return (
      <div className="patient-info mb-4 bg-light p-3 rounded">
        <Row>
          <Col xs="12">
            We have a {patientCase?.type === 'BOTH' ? 'Mother and Baby' : 'Mother'} suffering from the
            following:
          </Col>
          <Col xs="12">
            {patientCase?.motherSymptoms && patientCase?.motherSymptoms.length > 0 && (
              <div className="text-primary">Mother
                Symptoms: {patientCase?.motherSymptoms.map(symptom => symptom.enCondition).join(', ')}</div>
            )}
            {patientCase?.babySymptoms && patientCase?.babySymptoms.length > 0 && (
              <div className="text-primary">Baby
                Symptoms: {patientCase?.babySymptoms.map(symptom => symptom.enCondition).join(', ')}</div>
            )}
          </Col>
          <Col xs="12">
            Are you able to receive this patient?
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="location-selection-modal" size="lg">
      <ModalHeader toggle={toggle}>
        {popupType === PopupType.START ? 'Select Pickup Location' : 'Select Destination Location'}
      </ModalHeader>
      <ModalBody>
        <AvForm>
          {/* Location Type Selection */}
          <Row className="mb-4">
            {((popupType === PopupType.START && countrySettings?.allowVillagePickup) || (popupType === PopupType.END && journey.startVillage && countrySettings?.allowVillageDestination)) &&
              <Col xs="6">
                <Button
                  color={locationType === LocationType.VILLAGE ? 'success' : 'light'}
                  className="w-100"
                  onClick={() => onLocationTypeChange(LocationType.VILLAGE)}
                  type="button"
                >
                  <div className="d-flex justify-content-center align-items-center">
                    {locationType === LocationType.VILLAGE && (
                      <FontAwesomeIcon icon="check-circle" className="mr-2"/>
                    )}
                    {getLabel(RegionDescriptionType.VILLAGE)}
                  </div>
                </Button>
              </Col>
            }
            <Col xs="6">
              <Button
                color={locationType === LocationType.HEALTHCARE_FACILITY ? 'success' : 'light'}
                className="w-100"
                onClick={() => onLocationTypeChange(LocationType.HEALTHCARE_FACILITY)}
                type="button"
              >
                <div className="d-flex justify-content-center align-items-center">
                  {locationType === LocationType.HEALTHCARE_FACILITY && (
                    <FontAwesomeIcon icon="check-circle" className="mr-2"/>
                  )}
                  Health Facility
                </div>
              </Button>
            </Col>
          </Row>

          <Row>
            {(popupType !== PopupType.END || locationType !== LocationType.HEALTHCARE_FACILITY) ?
              <>
                <Col md="12">
                  <AvGroup>
                    <Label htmlFor="locationSearch">
                      {locationType === LocationType.HEALTHCARE_FACILITY ? 'Search for Healthcare Facility' : `Search for ${getLabel(RegionDescriptionType.VILLAGE)}`}
                    </Label>
                    <VillageAvTypeahead
                      labelKey="name"
                      valueKey="id"
                      data={optionList(false)}
                      placeholder={locationType === LocationType.HEALTHCARE_FACILITY ? 'Search for Healthcare Facility' : `Search for ${getLabel(RegionDescriptionType.VILLAGE)}`}
                      id="locationSearch"
                      clearValue={clearValue}
                      onChange={handleLocationSelection}
                      loading={isLoading}
                      input={{
                        required: true,
                        id: 'locationSearch',
                        name: 'locationId',
                      }}
                    />
                  </AvGroup>
                </Col>
                {selectedLocation && locationType === LocationType.VILLAGE && popupType === PopupType.END && showRouteDetails && (
                  <>
                    <Col md="12">
                      <AvGroup>
                        <Label id="distanceLabel" for="route-distance">
                          Distance (km)
                        </Label>
                        <AvField
                          id="route-distance"
                          type="text"
                          className="form-control"
                          name="distance"
                          onChange={(e) => setRouteDistance(parseFloat(e.target.value))}
                          value={routeDistance.toFixed(2)}
                        />
                      </AvGroup>
                    </Col>
                    <Col md="12">
                      <AvGroup>
                        <Label id="durationMinutesLabel" for="route-durationMinutes">
                          Duration (Minutes)
                        </Label>
                        <AvField
                          id="route-durationMinutes"
                          type="number"
                          className="form-control"
                          name="durationMinutes"
                          onChange={(e) => setDurationMinutes(parseFloat(e.target.value))}
                          value={durationMinutes.toFixed(1)}
                        />
                      </AvGroup>
                    </Col>
                    <Col md="12">
                      <AvGroup>
                        <Label id="priceLabel" for="overridePrice">
                          Current Price
                        </Label>
                        <AvField
                          id="overridePrice"
                          type="number"
                          className="form-control"
                          name="overridePrice"
                          onChange={(e) => setOverridePrice(toCents(parseFloat(e.target.value)))}
                          value={fromCents(currentPrice)}
                        />
                      </AvGroup>
                    </Col>
                  </>
                )}
              </>
              :
              <>
                <Col md="12">
                  <PatientInformation patientCase={journey?.patientCase}/>
                  {facilitySuggestions && facilitySuggestions.length > 0 && (
                    countrySettings?.useGeofence ? (
                      // Grouped by level when geofence is enabled
                      (() => {
                        const groupedFacilities = groupFacilitiesByLevel(facilitySuggestions);
                        const sortedLevels = Object.keys(groupedFacilities).map(Number).sort((a, b) => a - b);

                        return sortedLevels.map(level => (
                          <div key={level} className="mb-3">
                            <div
                              className="d-flex align-items-center cursor-pointer p-2 bg-light rounded"
                              onClick={() => toggleCollapseLevel(level)}
                              style={{cursor: 'pointer'}}
                            >
                              <FontAwesomeIcon
                                icon={collapsedLevels[level] ? "chevron-right" : "chevron-down"}
                                className="mr-2"
                              />
                              <h6 className="mb-0 font-weight-bold">HF Level {level}</h6>
                            </div>
                            <Collapse isOpen={!collapsedLevels[level]}>
                              <div className="mt-2">
                                {groupedFacilities[level].map((facilitySuggestion: FacilitySuggestion) => (
                                  <HealthFacilitySuggestion
                                    key={facilitySuggestion.facility.id}
                                    facility={facilitySuggestion.facility}
                                    distance={facilitySuggestion.distance}
                                    durationSeconds={facilitySuggestion.durationSeconds}
                                    isSelectLoading={loadingFacilityId === facilitySuggestion.facility.id}
                                    onSelect={() => {
                                      handleGeofenceFacilitySelect(facilitySuggestion);
                                    }}
                                    onReject={() => {
                                      handleHFRejection(facilitySuggestion.facility.id);
                                    }}
                                  />
                                ))}
                              </div>
                            </Collapse>
                          </div>
                        ));
                      })()
                    ) : (
                      // Regular list when geofence is disabled
                      facilitySuggestions.map((facilitySuggestion: FacilitySuggestion) => (
                        <HealthFacilitySuggestion
                          key={facilitySuggestion.facility.id}
                          facility={facilitySuggestion.facility}
                          distance={facilitySuggestion.distance}
                          durationSeconds={facilitySuggestion.durationSeconds}
                          onSelect={() => {
                            setSelectedLocation(facilitySuggestion.facility.id);
                            handleSave(facilitySuggestion.facility.id, facilitySuggestion.route, facilitySuggestion.routeCouncil);
                          }}
                          onReject={() => {
                            handleHFRejection(facilitySuggestion.facility.id);
                          }}
                        />
                      ))
                    )
                  )}

                  {hfNames.length === 0 && !isLoading && (
                    <div className="text-center py-4 text-muted">
                      No health facilities available. Please try a different selection.
                    </div>
                  )}
                </Col>
              </>
            }
          </Row>

          {isLoading && (
            <div className="text-center py-4">
              <FontAwesomeIcon icon="spinner" spin/> Loading...
            </div>
          )}

          {!isLoading && (!hfNames.length && !villageNames.length) && (
            <div className="text-center py-4 text-muted">
              No locations available for the selected country and type.
            </div>
          )}
        </AvForm>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Cancel</Button>{' '}
        <Button
          color="primary"
          onClick={() => handleSave(selectedLocation, existingRoute)}
          disabled={!selectedLocation || facilitiesLoading || villagesLoading || hfSuggestionsLoading || loadingData}
        >
          Use this {locationType === LocationType.HEALTHCARE_FACILITY ? 'facility' : 'location'}
        </Button>
      </ModalFooter>

      {/* Rejection Reason Modal */}
      <RRHFModal
        isOpen={showRRHFModal}
        toggle={toggleRRHFModal}
        patientCaseId={journey?.patientCase?.id || ''}
        hfId={rejectionHFId}
        onSuccess={toggleRRHFModal}
      />
    </Modal>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  compactHealthcareFacilities: storeState.healthcareFacility.compactEntities,
  facilitiesLoading: storeState.healthcareFacility.compactLoading,
  compactVillages: storeState.village.compactEntities,
  villagesLoading: storeState.village.compactLoading,
  countries: storeState.region.countries,
  userCountryId: storeState.authentication.userCountryId,
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN']),
  mmamaSettings: storeState.mmamaSettings.settings,
  startCouncilSuggestions: storeState.healthcareFacility.startCouncilSuggestions,
  startFacilitySuggestions: storeState.healthcareFacility.startFacilitySuggestions,
  startVillageSuggestions: storeState.healthcareFacility.startVillageSuggestions,
  hfSuggestionsLoading: storeState.healthcareFacility.loading,
  rejectedHealthcareFacilities: storeState.patientCaseRRHF.entities,
  rejectionReasonsHF: storeState.rejectionReasonHF.entities,
});

const mapDispatchToProps = {
  getHealthcareFacilitiesCompact,
  getVillagesCompact,
  getCountries,
  getMMamaSettings,
  getStartCouncilSuggestions,
  getStartFacilitySuggestions,
  getStartVillageSuggestions,
  resetHealthcareFacility,
  resetVillage,
  getRejectedFacilitiesByPatientCase,
  getRejectionReasons,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

const LocationSelectionModal = connect(mapStateToProps, mapDispatchToProps)(LocationSelectionModalComponent);

export default LocationSelectionModal;
